import { Helmet } from "react-helmet-async";
import { gsap } from "../animations/gsapSetup.ts";
import { SoccerGoalSvg } from "../components/sports/SoccerGoalSvg.tsx";
import { useGsapScrollTrigger } from "../hooks/useGsapScrollTrigger.ts";
import { useReducedMotion } from "../hooks/useReducedMotion.ts";
import { useI18n } from "../i18n/context.tsx";
import { SITE_URL } from "../utils/constants.ts";
import styles from "./SportClassic.module.css";

const VOICE_BARS = [18, 32, 24, 40, 16, 36, 28, 20, 34, 22, 30, 14];

export function SoccerClassicPage() {
  const { t } = useI18n();
  const reducedMotion = useReducedMotion();

  const heroRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const title = el.querySelector("[data-hero-title]");
    const sub = el.querySelector("[data-hero-sub]");
    if (title) {
      gsap.set(title, { scale: 0.85, opacity: 0 });
      gsap.to(title, { scale: 1, opacity: 1, duration: 1.2, ease: "power3.out", delay: 0.2 });
    }
    if (sub) {
      gsap.set(sub, { y: 30, opacity: 0 });
      gsap.to(sub, { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.6 });
    }
  }, []);

  const voiceRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const bars = el.querySelectorAll("[data-bar]");
    gsap.set(bars, { scaleY: 0.2, opacity: 0 });
    gsap.to(bars, {
      scaleY: 1,
      opacity: 1,
      duration: 0.6,
      stagger: 0.05,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  const analyticsRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    gsap.set(el, { y: 40, opacity: 0 });
    gsap.to(el, {
      y: 0,
      opacity: 1,
      duration: 0.8,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 80%", toggleActions: "play none none reverse" },
    });
  }, []);

  const featRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    const cards = el.querySelectorAll("[data-feat]");
    gsap.set(cards, { y: 40, opacity: 0 });
    gsap.to(cards, {
      y: 0,
      opacity: 1,
      duration: 0.6,
      stagger: 0.1,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 75%", toggleActions: "play none none reverse" },
    });
  }, []);

  const ctaRef = useGsapScrollTrigger((el) => {
    if (reducedMotion) return;
    gsap.set(el, { scale: 0.95, opacity: 0 });
    gsap.to(el, {
      scale: 1,
      opacity: 1,
      duration: 0.7,
      ease: "power3.out",
      scrollTrigger: { trigger: el, start: "top 80%", toggleActions: "play none none reverse" },
    });
  }, []);

  return (
    <>
      <Helmet>
        <title>ShootCreator Soccer Classic - Voice-Controlled Shot Logger</title>
        <meta
          name="description"
          content="Soccer shooting practice logger with 6-zone goal analytics. Voice-controlled counting, PK tracker, Apple Watch. Free."
        />
        <link rel="canonical" href={`${SITE_URL}/soccer/classic`} />
      </Helmet>

      <section ref={heroRef} className={`${styles.hero} ${styles.heroBgSoccer}`}>
        <h1 className={`${styles.heroWordmark} ${styles.accentSoccer}`} data-hero-title>
          {t.soccer.heroWordmark}
        </h1>
        <p className={styles.heroSubtitle} data-hero-sub>
          {t.soccer.heroSubtitle}
        </p>
      </section>

      <section ref={voiceRef} className={`${styles.section} ${styles.sectionAlt}`}>
        <h2 className={styles.sectionTitle}>{t.soccer.voiceTitle}</h2>
        <p className={styles.sectionDesc}>{t.soccer.voiceDesc}</p>
        <div className={styles.voiceBars}>
          {VOICE_BARS.map((h, i) => (
            <div
              key={`b${String(i)}`}
              className={styles.voiceBar}
              data-bar
              style={{ height: h, background: "#2196f3" }}
            />
          ))}
        </div>
      </section>

      <section ref={analyticsRef} className={styles.section}>
        <h2 className={styles.sectionTitle}>{t.soccer.analyticsTitle}</h2>
        <p className={styles.sectionDesc}>{t.soccer.analyticsDesc}</p>
        <div className={styles.svgWrap}>
          <SoccerGoalSvg />
        </div>
      </section>

      <section ref={featRef} className={`${styles.section} ${styles.sectionAlt}`}>
        <h2 className={styles.sectionTitle}>{t.soccer.classicTitle}</h2>
        <div className={styles.featGrid}>
          {t.soccer.features.map((f) => (
            <div key={f.title} className={styles.featCard} data-feat>
              <span className={styles.featIcon}>{f.icon}</span>
              <h3 className={styles.featTitle}>{f.title}</h3>
              <p className={styles.featDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section ref={ctaRef} className={styles.cta}>
        <h2 className={styles.ctaTitle}>{t.soccer.ctaTitle}</h2>
        <button type="button" className={`${styles.ctaButton} ${styles.ctaButtonSoccer}`}>
          {t.soccer.ctaButton}
        </button>
        <p className={styles.ctaNote}>{t.soccer.ctaNote}</p>
      </section>
    </>
  );
}
